import { addGraphic, addGraphicLayer, addGraphicToMap, createGeometryPoint, createGeometryPolyline, createGeometryPolygon, unionListGeometries, webMercatorToGeographicGeometry, calculateAreaUsingGeodesicArea, GeographicTowebMercatorGeometry, distanceGeometrys, bufferGeometry, geometryServiceProjection, isTouches } from "../../utils/graghics";
export function graphicToMap(settings) {
    addGraphicToMap({
        feature: settings.feature,
        mapView: settings.mapView,
        clear: settings.clear,
        polygonSymbol: settings.polygonSymbol,
        polylineSymbol: settings.polylineSymbol,
        pointSymbol: settings.pointSymbol,
    });
}
/**
 * @category geometry manipulations
 * @example
* ```typescript
let point = {
                x: 41.005695222999975,
                y: 30.989947433999987,
                spatialReference: 4326
            }
    let geometryPoint = await momraGisCore.createPoint(point);
*```
 */
export function createPoint(point) {
    return createGeometryPoint(point);
}
/**
 * @category geometry manipulations
 * @version 1.0.2 and above
 * @example
 * ```typescript
 * let paths = [
[  // first path
   [-97.06138,32.837,5],
   [-97.06133,32.836,6],
   [-97.06124,32.834,7]
], [  // second path
   [-97.06326,32.759],
   [-97.06298,32.755]
]
];
 * let polyline = {
 *     paths: paths,
 *     hasZ : true,     //Indicates if the geometry has Z values boolean true or false
 *     hasM : true,     //Indicates if the geometry has M values boolean true or false
 *     spatialReference : 102100 or 4236
 * }
 * ```
 */
export function createPolyline(polyline) {
    return createGeometryPolyline(polyline);
}
/**
* @category geometry manipulations
* @version 1.0.2 and above
* @example
* ```typescript
*const rings = [
      [  // first ring
          [-97.06138,32.837,35.1,4.8],
          [-97.06133,32.836,35.2,4.1],
          [-97.06124,32.834,35.3,4.2],
          [-97.06138,32.837,35.1,4.8]  // same as first vertex
      ], [  // second ring
          [-97.06326,32.759,35.4],
          [-97.06298,32.755,35.5],
          [-97.06153,32.749,35.6],
          [-97.06326,32.759,35.4]  // same as first vertex
      ]
  ];
* let polygon = {
*     hasZ : true,     //Indicates if the geometry has Z values boolean true or false
*     hasM : true,     //Indicates if the geometry has M values boolean true or false
*     rings: rings,
*     spatialReference : 102100 or 4326
* }
* ```
*/
export function createPolygon(polygon) {
    return createGeometryPolygon(polygon);
}
/**
 * @category geometry manipulations
 * @param geometries  Geometry[] An array of Geometries.
 * All inputs must be of the same type of geometries and share one spatial reference.
 * @version 1.0.2 and above
 */
export function unionGeometries(geometries) {
    return unionListGeometries(geometries);
}
/**
 * @category geometry manipulations
 * @version 1.0.5 and above
 */
export function touchesGeometry(geometry1, geometry2) {
    return isTouches(geometry1, geometry2);
}
/**
 * @example
 * ```typescript
 * let graghicLayer = await momraGisCore.createGraphicLayer('<layerName>', mapView);
 * ```
 * @param layerName
 * @param mapView
 * @returns graghicLayer
 */
export function createGraphicLayer(layerName, mapView) {
    return addGraphicLayer(layerName, mapView);
}
export function addGraphicToLayer(addGraghicToLayer) {
    addGraphic(addGraghicToLayer);
}
/**
 * @category geometry conversions
 * @version 1.0.2 and above
 */
export function webMercatorToGeographic(geometry) {
    return webMercatorToGeographicGeometry(geometry);
}
/**
* @category geometry conversions
* @version 1.0.2 and above
*/
export function GeographicTowebMercator(geometry) {
    return GeographicTowebMercatorGeometry(geometry);
}
/**
* @category geometry calculations
* @version 1.0.2 and above
*
* working with GeodesicArea to get input geometry area and ArealUnits type is string or number
* Possible Values for arealUnits:"acres"|"ares"|"hectares"|"square-feet"|"square-meters"|"square-yards"|"square-kilometers"|"square-miles"|Number
*/
export function calculateArea(geometry, arealUnits) {
    return calculateAreaUsingGeodesicArea(geometry, arealUnits);
}
/**
 * @category geometry calculations
 * @version 1.0.2 and above
 *
 * working with distance function in geometryEngineAsync
 * Possible Values for distanceUnit:"meters"|"feet"|"kilometers"|"miles"|"nautical-miles"|"yards"|Number
 */
export function distanceBetweeanGeometrys(geometry1, geometry2, distanceUnit) {
    return distanceGeometrys(geometry1, geometry2, distanceUnit);
}
/**
 * @category geometry calculations
 * @version 1.0.2 and above
 *
 * working with buffer function in geometryEngineAsync
 */
export function buffer(geometry, distance, unit, unionResults) {
    return bufferGeometry(geometry, distance, unit, unionResults);
}
/**
  * @category geometry manipulations
  * @param url the rest service under this path ==> /services/Utilities/Geometry/GeometryServer/project
  * @version 1.0.5 and above
  * @example
  * ```typescript
  *  let outSpatialReference = {
            wkid: outSR //4326
     }

    let projectParams = {
        geometries: geometries,
        outSpatialReference: outSpatialReference
    }
    let projectedGeometries = await momraGisCore.projectionWithGeometryService(url, projectParams);
  * ```
  */
export function projectionWithGeometryService(url, projectParameters) {
    return geometryServiceProjection(url, projectParameters);
}
/**
 * @category utilities
 * @version 1.0.5 and above  *
 * @example
 * ```typescript
 *  let inSR = {
               wkid: parseInt(spatialReference)
           }
   let geometries = [];
   let url = 'https://webgis.momra.gov.sa/server/rest/services/Utilities/Geometry/GeometryServer/project';
 * let projectedGeometry = await geometryProjection(url, inSR, geometries);
 * ```
 * this function based on {@link projectionWithGeometryService} function
 */
export async function geometryProjection(url, outSR, geometries) {
    let outSpatialReference = {
        wkid: outSR
    };
    let projectParams = {
        geometries: geometries,
        outSpatialReference: outSpatialReference
    };
    let projectedGeometry = await projectionWithGeometryService(url, projectParams);
    return projectedGeometry;
}
